// Couche d'accès au planning — source unique : la console (GET /api/planning).
//
// Filet de secours : à chaque lecture réussie, le planning est rangé dans le navigateur,
// et le serveur en dépose aussi une copie à côté du site (/planning-secours.json). Si le
// serveur ne répond plus (incident d'hébergement, redémarrage), la page affiche ces
// horaires-là avec leur date au lieu d'un écran vide — seules les inscriptions en ligne
// sont alors indisponibles.
import { useCallback, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const CLE_SECOURS = "1000arts-planning-secours";

let cache = null;
let inflight = null;

// Attentes entre les tentatives. Au redémarrage du serveur (chaque déploiement), le
// planning met quelques secondes à être rechargé : un seul essai affichait « planning
// momentanément indisponible » à qui visitait le site pile à ce moment-là. On réessaie
// donc en silence pendant ~8 s avant d'afficher quoi que ce soit d'inquiétant.
const ATTENTES_MS = [0, 800, 2500, 5000];

const pause = (ms) => new Promise((r) => setTimeout(r, ms));

const aujourdhui = () => new Date().toISOString().slice(0, 10);

function ranger(data) {
  try {
    localStorage.setItem(CLE_SECOURS, JSON.stringify({ ...data, secours_le: aujourdhui() }));
  } catch (e) {
    /* navigateur sans stockage : on se contente de la copie servie par le site */
  }
}

async function secours() {
  try {
    const local = JSON.parse(localStorage.getItem(CLE_SECOURS) || "null");
    if (local?.courses?.length) return { ...local, secours: true };
  } catch (e) {
    /* stockage illisible : on tente la copie du site */
  }
  const r = await fetch(`/${"planning-secours.json"}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`secours-${r.status}`);
  const copie = await r.json();
  if (!copie?.courses?.length) throw new Error("secours-vide");
  return { ...copie, secours: true };
}

export function fetchPlanning(force = false) {
  if (!force && cache) return Promise.resolve(cache);
  if (!force && inflight) return inflight;
  inflight = (async () => {
    let derniereErreur;
    for (const attente of ATTENTES_MS) {
      if (attente) await pause(attente);
      try {
        const r = await fetch(`${API}/api/planning`);
        if (!r.ok) throw new Error(`planning-${r.status}`);
        cache = await r.json();
        ranger(cache);
        return cache;
      } catch (e) {
        derniereErreur = e;
      }
    }
    try {
      return await secours();
    } catch (e) {
      throw derniereErreur || e;
    }
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

const EMPTY_FILTERS = { days: [], disciplines: [], levels: [], teachers: [], rooms: [] };

const depuis = (d) => ({
  courses: d.courses || [],
  filters: d.filters || EMPTY_FILTERS,
  semestre: d.semestre || "",
  secours: !!d.secours,
  secours_le: d.secours ? d.secours_le || "" : "",
  loading: false,
  error: null,
});

export function usePlanning() {
  const [state, setState] = useState(() => ({
    courses: cache?.courses || [],
    filters: cache?.filters || EMPTY_FILTERS,
    semestre: cache?.semestre || "",
    secours: false,
    secours_le: "",
    loading: !cache,
    error: null,
  }));

  const charger = useCallback((force = false) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    return fetchPlanning(force)
      .then((d) => setState(depuis(d)))
      .catch(() => setState((s) => ({ ...s, loading: false, error: "planning" })));
  }, []);

  useEffect(() => {
    let alive = true;
    fetchPlanning()
      .then((d) => alive && setState(depuis(d)))
      .catch(() => alive && setState((s) => ({ ...s, loading: false, error: "planning" })));
    return () => { alive = false; };
  }, []);

  // Guérison silencieuse. Une publication du site redémarre le serveur : pendant une
  // trentaine de secondes, le planning répond 503 et la page affiche les horaires de
  // secours avec un avertissement. Plutôt que de laisser un bandeau inquiétant sur une
  // page que personne ne rechargera, on réessaie en arrière-plan et on remplace les
  // horaires dès que le serveur répond — sans rien demander au visiteur.
  useEffect(() => {
    if (!state.secours && !state.error) return undefined;
    let alive = true;
    let minuteur;
    const essais = [5000, 10000, 20000, 30000, 60000];
    let n = 0;
    const reessayer = () => {
      minuteur = setTimeout(async () => {
        if (!alive) return;
        try {
          const d = await fetchPlanning(true);
          if (alive && !d.secours) {
            setState(depuis(d));
            return;
          }
        } catch (e) {
          /* le serveur n'est pas encore revenu : on retentera */
        }
        if (alive && n < essais.length - 1) {
          n += 1;
          reessayer();
        }
      }, essais[n]);
    };
    reessayer();
    return () => { alive = false; clearTimeout(minuteur); };
  }, [state.secours, state.error]);

  return { ...state, reload: () => charger(true) };
}
